/**
 * Lista de hosts do app.
 *
 * O cockpit sempre foi cliente magro: o daemon e que executa os agentes. Ter
 * uma lista de hosts em vez de um endereco unico e o que torna o app
 * independente -- ele conecta na VPS que voce quiser, e tambem num daemon
 * local, sem que nenhum deles seja "o" servidor.
 *
 * Senha NAO e guardada. Fica so o token de sessao, que expira; quando expira,
 * relogin. O usuario e lembrado apenas para nao precisar digitar de novo.
 */

export type HostKind = 'remote' | 'local';

export interface Host {
  id: string;
  name: string;
  /** Vazio = mesma origem da pagina (caso da versao web). */
  url: string;
  kind: HostKind;
  /** Token de sessao. Some quando expira ou no logout. */
  token: string | null;
  tokenExpiresAt: string | null;
  /** Ultimo usuario usado, para pre-preencher o formulario. */
  username: string | null;
}

const STORAGE_HOSTS = 'cm.hosts';
const STORAGE_ACTIVE = 'cm.activeHostId';

// Chaves da versao anterior, quando havia um unico daemon.
const LEGACY_URL = 'cm.daemonUrl';
const LEGACY_TOKEN = 'cm.token';

export function newHostId(): string {
  return `h_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Nome sugerido a partir da URL, para o usuario nao ter que inventar um. */
export function suggestName(url: string): string {
  if (!url.trim()) return 'Este servidor';
  try {
    return new URL(url).hostname;
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0] || 'Servidor';
  }
}

function sanitizeUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function isHost(value: unknown): value is Host {
  if (!value || typeof value !== 'object') return false;
  const h = value as Partial<Host>;
  return typeof h.id === 'string' && typeof h.name === 'string' && typeof h.url === 'string';
}

function normalize(value: Partial<Host>): Host {
  return {
    id: typeof value.id === 'string' ? value.id : newHostId(),
    name: typeof value.name === 'string' && value.name.trim() ? value.name : 'Servidor',
    url: sanitizeUrl(typeof value.url === 'string' ? value.url : ''),
    kind: value.kind === 'local' ? 'local' : 'remote',
    token: typeof value.token === 'string' ? value.token : null,
    tokenExpiresAt: typeof value.tokenExpiresAt === 'string' ? value.tokenExpiresAt : null,
    username: typeof value.username === 'string' ? value.username : null,
  };
}

/**
 * Migra a configuracao antiga (um daemon so) para a lista.
 *
 * Sem isso, quem atualiza o app perderia o login e o endereco configurado --
 * a atualizacao automatica viraria uma regressao visivel.
 */
export function migrateLegacy(builtInUrl: string): Host[] {
  const legacyUrl = localStorage.getItem(LEGACY_URL);
  const legacyToken = localStorage.getItem(LEGACY_TOKEN);

  // A URL pode ser string vazia de proposito (versao web, mesma origem), por
  // isso testamos `!== null` em vez de checar se e truthy.
  const url = sanitizeUrl(legacyUrl !== null ? legacyUrl : builtInUrl);

  const host: Host = {
    id: newHostId(),
    name: suggestName(url),
    url,
    kind: 'remote',
    token: legacyToken,
    tokenExpiresAt: null,
    username: null,
  };

  localStorage.removeItem(LEGACY_URL);
  localStorage.removeItem(LEGACY_TOKEN);
  return [host];
}

export function loadHosts(builtInUrl: string): Host[] {
  try {
    const raw = localStorage.getItem(STORAGE_HOSTS);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const hosts = parsed.filter(isHost).map(normalize);
        if (hosts.length > 0) return hosts;
      }
    }
  } catch {
    /* JSON corrompido: recomeca da migracao */
  }
  return migrateLegacy(builtInUrl);
}

export function saveHosts(hosts: Host[]): void {
  try {
    localStorage.setItem(STORAGE_HOSTS, JSON.stringify(hosts));
  } catch {
    /* sem persistencia; vale para esta sessao */
  }
}

export function loadActiveHostId(hosts: Host[]): string | null {
  const saved = localStorage.getItem(STORAGE_ACTIVE);
  if (saved && hosts.some((h) => h.id === saved)) return saved;
  return hosts[0]?.id ?? null;
}

export function saveActiveHostId(id: string | null): void {
  if (id) localStorage.setItem(STORAGE_ACTIVE, id);
  else localStorage.removeItem(STORAGE_ACTIVE);
}

/** Token utilizavel: existe e ainda nao expirou. */
export function hasValidToken(host: Host | null | undefined): boolean {
  if (!host?.token) return false;
  if (!host.tokenExpiresAt) return true; // token antigo, sem validade registrada
  return new Date(host.tokenExpiresAt).getTime() > Date.now();
}

export function upsertHost(hosts: Host[], host: Host): Host[] {
  const index = hosts.findIndex((h) => h.id === host.id);
  if (index === -1) return [...hosts, host];
  return hosts.map((h) => (h.id === host.id ? host : h));
}

export function removeHost(hosts: Host[], id: string): Host[] {
  return hosts.filter((h) => h.id !== id);
}

/** Rotulo curto para a barra: o nome, ou o host da URL. */
export function hostLabel(host: Host): string {
  return host.name || suggestName(host.url);
}
